import React, { useEffect, useState } from 'react';
import { FileTypeBadge } from '../components/shared/FileTypeBadge';
import { IdentificationRule } from '../config';
import { getIdentificationRules } from '../services/ruleService';
import { resources } from '../resources';
import { FileSearch } from 'lucide-react';

export const IdentificationRules: React.FC = () => {
  const [rules, setRules] = useState<IdentificationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    let active = true;
    setLoading(true);
    getIdentificationRules()
      .then((data) => {
        if (active) {
          setRules(data);
          setError(undefined);
        }
      })
      .catch(() => {
        if (active) setError(resources.identificationRules.loadError);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-bold text-foreground">
          {resources.identificationRules.title}
        </h1>
        <p className="text-muted-foreground">
          {resources.identificationRules.subtitle}
        </p>
      </header>

      <div className="inline-flex items-center gap-3 bg-card p-4 rounded-2xl border border-border shadow-sm">
        <div className="bg-primary/10 p-2 rounded-lg">
          <FileSearch className="w-5 h-5 text-primary" />
        </div>
        <span className="text-sm font-medium text-muted-foreground">
          {resources.identificationRules.summary(rules.length)}
        </span>
      </div>

      <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="px-6 py-20 text-center text-muted-foreground">
            <div className="flex flex-col items-center gap-3">
              <FileSearch className="w-10 h-10 text-border animate-pulse" />
              <p className="font-medium">{resources.identificationRules.loading}</p>
            </div>
          </div>
        ) : error ? (
          <div className="px-6 py-20 text-center text-destructive">
            <div className="flex flex-col items-center gap-3">
              <FileSearch className="w-10 h-10 text-destructive/50" />
              <p className="font-medium">{error}</p>
            </div>
          </div>
        ) : rules.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-muted/50 border-b border-border">
                  <th className="px-6 py-4 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                    {resources.identificationRules.table.pathPrefix}
                  </th>
                  <th className="px-6 py-4 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                    {resources.identificationRules.table.fileType}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rules.map((rule, idx) => (
                  <tr key={idx} className="hover:bg-muted/50 transition-colors">
                    <td className="px-6 py-4">
                      <code className="text-sm font-mono text-foreground bg-muted px-2 py-1 rounded-lg">
                        {rule.pathPrefix}
                      </code>
                    </td>
                    <td className="px-6 py-4">
                      <FileTypeBadge type={rule.fileType} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-6 py-20 text-center text-muted-foreground">
            <div className="flex flex-col items-center gap-3">
              <FileSearch className="w-10 h-10 text-border" />
              <p className="font-medium">
                {resources.identificationRules.noRules}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
